import {Form} from 'antd';
import {DefaultOptionType} from 'antd/lib/select';
import dayjs, {Dayjs} from 'dayjs';
import {isEqual, isNil} from 'lodash';
import {NoUndefinedRangeValueType} from 'rc-picker/lib/PickerInput/RangePicker';
import {KeyboardEvent, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useLicenses} from '../../../../../provider/license/license-hook';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {usePersonality} from '../../../../../provider/personality/use-personality';
import {useReviewsDoctorList} from '../../../../../service/reviews/reviews-doctors';
import {useReviewsReplierList} from '../../../../../service/reviews/reviews-repliers';
import {useResponsibleUsers} from '../../../../../service/reviews/reviews-responsible-users';
import {useReviewsStatistic} from '../../../../../service/reviews/reviews-statistic';
import {ReviewStatusType, useReviewStatusList} from '../../../../../service/reviews/reviews-statuses';
import {FeaturesEnum} from '../../../../../service/user/user-type';
import {debounce} from '../../../../../util/function';
import {useUrl} from '../../../../../util/url-hook/url-hook';
import {
    ReviewsSavedFilterPeriodPickerTabType,
    ReviewsSavedFilterQueryType,
    ReviewsSavedFilterUiStateType,
} from '../../reviews-state/reviews-saved-filter-helper';
import {defaultReviewsState} from '../../reviews-state/reviews-state-const';
import {reviewsFilterStateToUrlObject} from '../../reviews-state/reviews-state-helper';
import {useReviewStateHook} from '../../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../../reviews-state/reviews-state-type';
import {
    getRatingListCountForKey,
    getWeightedAverageRatingForSelectedRatings,
} from '../reviews-statistic/reviews-statistic-helper';

import {applyReviewFilterDateFieldsToForm, getReviewsFilterDataFromForm, trackAnalytics} from './reviews-filter-helper';
import {
    AbuseStatusEnum,
    AutoAbuseEnum,
    AutoReplayEnum,
    ContentOfReviewsEnum,
    DeletedReviewsEnum,
    DoctorsReviewsEnum,
    EmployeesReviewsEnum,
    HasNotesEnum,
    HiddenReviewsEnum,
    ModeratedEnum,
    PossibilityOfAnAnswerEnum,
    RepliesToReviewsEnum,
    ReplyPublishedEnum,
    ReviewChangedEnum,
    ReviewNameEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
    ReviewsTonalityFilterEnum,
    WaitingForSecondAnswerEnum,
    WithoutTagEnum,
} from './reviews-filter-type';

const RATING_FILTER_ORDER = [5, 4, 3, 2, 1, 0];
const SAVED_FILTER_SAVE_DEBOUNCE = 500;
const ANALYTICS_ALL_SELECTED_LABEL = 'all selected';

type CustomOptionType = DefaultOptionType & {
    statusInfo: ReviewStatusType;
};

export enum ReviewsFilterSectionEnum {
    main = 'main',
    employee = 'employee',
    tags = 'tags',
    additional = 'additional',
}

const ALL_SECTION_KEYS = Object.values(ReviewsFilterSectionEnum);

type ReviewsFilterApplyModeType = 'immediate' | 'deferred';

type UseReviewsFilterOptionsType = {
    applyMode?: ReviewsFilterApplyModeType;
    onDrawerClose?: () => void;
};

function getExpandedSectionsValue(isSavedFilterEnabled: boolean, savedExpandedSections?: Array<string>): Array<string> {
    if (!isSavedFilterEnabled) {
        return [ReviewsFilterSectionEnum.main];
    }

    return savedExpandedSections ?? ALL_SECTION_KEYS;
}

type ReviewsFilterMainSectionActiveInputType = {
    reviewFilterState: ReviewsFilterDataType;
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
    collectionDateFrom: Dayjs | null;
    collectionDateTo: Dayjs | null;
    hasDoctors: boolean | null | undefined;
};

function isReviewsFilterMainSectionDateOrSourceActive(
    reviewFilterState: ReviewsFilterDataType,
    dateFrom: Dayjs | null,
    dateTo: Dayjs | null,
    collectionDateFrom: Dayjs | null,
    collectionDateTo: Dayjs | null
): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.sourceList].length > 0 ||
        (Boolean(dateFrom) && Boolean(dateTo)) ||
        (Boolean(collectionDateFrom) && Boolean(collectionDateTo))
    );
}

function isReviewsFilterMainSectionRestActive(
    reviewFilterState: ReviewsFilterDataType,
    hasDoctors: boolean | null | undefined
): boolean {
    return (
        (Boolean(hasDoctors) && reviewFilterState[ReviewsFilterFieldNameEnum.doctorNames].length > 0) ||
        reviewFilterState[ReviewsFilterFieldNameEnum.processStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.repliesToReviews] !== RepliesToReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.contentOfReviews] !== ContentOfReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer] !== PossibilityOfAnAnswerEnum.noSelected
    );
}

function isReviewsFilterMainSectionActive(input: ReviewsFilterMainSectionActiveInputType): boolean {
    const {reviewFilterState, dateFrom, dateTo, collectionDateFrom, collectionDateTo, hasDoctors} = input;

    return (
        isReviewsFilterMainSectionDateOrSourceActive(
            reviewFilterState,
            dateFrom,
            dateTo,
            collectionDateFrom,
            collectionDateTo
        ) || isReviewsFilterMainSectionRestActive(reviewFilterState, hasDoctors)
    );
}

function isReviewsFilterEmployeeSectionActive(
    reviewFilterState: ReviewsFilterDataType,
    replyDateFrom: Dayjs | null,
    replyDateTo: Dayjs | null
): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponsible].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponding].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.agencyRepliers].length > 0 ||
        (Boolean(replyDateFrom) && Boolean(replyDateTo))
    );
}

function isReviewsFilterTagsSectionActive(reviewFilterState: ReviewsFilterDataType): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.withoutTag] !== WithoutTagEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.tagList].length > 0
    );
}

// eslint-disable-next-line complexity
function isReviewsFilterAdditionalSectionActive(reviewFilterState: ReviewsFilterDataType): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.hasNotes] !== HasNotesEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.hiddenReviews] !== HiddenReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.deletedReviews] !== DeletedReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.abuseStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.sentiments].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.autoReplay].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.autoAbuse] !== AutoAbuseEnum.notSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewChanged] !== ReviewChangedEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.waitingForSecondAnswer] !==
            WaitingForSecondAnswerEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewType] !== ReviewNameEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewModerated] !== ModeratedEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.replyPublished] !== ReplyPublishedEnum.noSelected
    );
}

// eslint-disable-next-line max-statements, @typescript-eslint/explicit-module-boundary-types, sonarjs/cognitive-complexity
export function useReviewsFilter(options?: UseReviewsFilterOptionsType) {
    const applyMode: ReviewsFilterApplyModeType = options?.applyMode ?? 'immediate';
    const isDeferredApply = applyMode === 'deferred';
    const onDrawerClose = options?.onDrawerClose;

    const {
        reviewsState,
        isShowAutotags,
        reviewsCatalogListIsInProgress,
        reviewsCatalogList,
        setReviewsStateUrlQuery,
        isSavedFilterEnabled,
        isSavedFilterLoading,
        isSavedFilterSaving,
        savedFilterUiState,
        enableSavedFilter,
        disableSavedFilter,
        saveCurrentFilterState,
        resetSavedFilter,
    } = useReviewStateHook();

    const {getLocalizedString, localeName} = useLocale();
    const [form] = Form.useForm();
    const {licenses} = useLicenses();
    const hasMedicineCatalog = reviewsCatalogList?.some((catalog) => catalog.is_medicine);
    const internalAgencyReviewLicense = licenses[FeaturesEnum.bulkGenerateButton]?.isActive;
    const {data: reviewsReplierList, isFetching: reviewsReplierListIsInProgress} = useReviewsReplierList();
    const {data: reviewsDoctorList, isFetching: reviewsDoctorListIsInProgress} = useReviewsDoctorList(
        Boolean(hasMedicineCatalog)
    );
    const {data: reviewStatusList, isFetching: isReviewStatusListInProgress} = useReviewStatusList(localeName);
    const {queries} = useUrl<Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType>();
    const {filterState: reviewFilterState} = reviewsState;

    const [draftRevision, setDraftRevision] = useState<number>(0);

    const bumpDraftRevision = useCallback((): void => {
        setDraftRevision((previousRevision) => previousRevision + 1);
    }, []);

    const activeFilterStateForUi = useMemo((): ReviewsFilterDataType => {
        if (!isDeferredApply) {
            return reviewFilterState;
        }

        return getReviewsFilterDataFromForm(form);
        // draftRevision forces recomputation when draft form changes without URL update
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftRevision, form, isDeferredApply, reviewFilterState]);

    const reviewsStateWithAnyRating = useMemo(
        () => ({
            ...reviewsState,
            filterState: {
                ...activeFilterStateForUi,
                [ReviewsFilterFieldNameEnum.ratingList]: [],
            },
        }),
        [activeFilterStateForUi, reviewsState]
    );
    const {data: reviewsStatisticResult, isFetching: isReviewsStatisticFetching} =
        useReviewsStatistic(reviewsStateWithAnyRating);

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const filterKey = useMemo(() => ({filterKey: mainFilterKey}), [mainFilterKey]);
    const {data: responsibleUsersList, isFetching: isReviewResponsibleUsersInProgress} = useResponsibleUsers(filterKey);

    const dateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom];
    const dateFrom = dateFromRaw ? dayjs(dateFromRaw) : null;
    const dateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.dateTo];
    const dateTo = dateToRaw ? dayjs(dateToRaw) : null;
    const collectionDateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom];
    const collectionDateFrom = collectionDateFromRaw ? dayjs(collectionDateFromRaw) : null;
    const collectionDateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo];
    const collectionDateTo = collectionDateToRaw ? dayjs(collectionDateToRaw) : null;
    const replyDateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.replyDateFrom];
    const replyDateFrom = replyDateFromRaw ? dayjs(replyDateFromRaw) : null;
    const replyDateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.replyDateTo];
    const replyDateTo = replyDateToRaw ? dayjs(replyDateToRaw) : null;
    const today = dayjs().format('YYYY-MM-DD');
    const thirtyDaysAgo = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    const {isBigBrand} = usePersonality();

    const defaultReviewsStateMagnitIncluding = useMemo(
        () =>
            isBigBrand
                ? {
                      ...defaultReviewsState.filterState,
                      [ReviewsFilterFieldNameEnum.dateFrom]: new Date(`${thirtyDaysAgo}T00:00:00`),
                      [ReviewsFilterFieldNameEnum.dateTo]: new Date(`${today}T00:00:00`),
                  }
                : defaultReviewsState.filterState,
        [isBigBrand, thirtyDaysAgo, today]
    );

    const [periodPickerActiveTab, setPeriodPickerActiveTab] = useState<ReviewsSavedFilterPeriodPickerTabType>(
        savedFilterUiState.periodPickerActiveTab
    );

    const [expandedSections, setExpandedSections] = useState<Array<string>>(
        getExpandedSectionsValue(isSavedFilterEnabled, savedFilterUiState.expandedSections)
    );

    const [draftSavedFilterEnabled, setDraftSavedFilterEnabled] = useState<boolean>(isSavedFilterEnabled);

    const displaySavedFilterEnabled = isDeferredApply ? draftSavedFilterEnabled : isSavedFilterEnabled;

    const syncFormFromAppliedState = useCallback((): void => {
        form.setFieldsValue(reviewFilterState);
        applyReviewFilterDateFieldsToForm(form, reviewFilterState);
        setPeriodPickerActiveTab(savedFilterUiState.periodPickerActiveTab);
        setExpandedSections(getExpandedSectionsValue(isSavedFilterEnabled, savedFilterUiState.expandedSections));
        setDraftSavedFilterEnabled(isSavedFilterEnabled);
        bumpDraftRevision();
    }, [
        bumpDraftRevision,
        form,
        isSavedFilterEnabled,
        reviewFilterState,
        savedFilterUiState.expandedSections,
        savedFilterUiState.periodPickerActiveTab,
    ]);

    const isFilterEmptyForUi = useMemo(
        () => isEqual(activeFilterStateForUi, defaultReviewsStateMagnitIncluding),
        [activeFilterStateForUi, defaultReviewsStateMagnitIncluding]
    );

    const getCurrentSavedFilterUiState = useCallback(
        (
            nextPeriodPickerActiveTab = periodPickerActiveTab,
            nextExpandedSections = expandedSections
        ): ReviewsSavedFilterUiStateType => ({
            ...savedFilterUiState,
            periodPickerActiveTab: nextPeriodPickerActiveTab,
            expandedSections: nextExpandedSections,
        }),
        [expandedSections, periodPickerActiveTab, savedFilterUiState]
    );

    function handleSectionsChange(keys: string | Array<string>) {
        const next = Array.isArray(keys) ? keys : [keys];

        setExpandedSections(next);

        if (isDeferredApply) {
            return;
        }

        saveCurrentFilterState(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState(periodPickerActiveTab, next)
        );
    }

    const saveCurrentFilterStateDebounced = useMemo(
        () =>
            debounce((query: ReviewsSavedFilterQueryType, uiState: ReviewsSavedFilterUiStateType) => {
                saveCurrentFilterState(query, uiState);
            }, SAVED_FILTER_SAVE_DEBOUNCE),
        [saveCurrentFilterState]
    );

    function onFormReset() {
        const defaultFilterQuery = reviewsFilterStateToUrlObject(defaultReviewsStateMagnitIncluding);

        setReviewsStateUrlQuery({
            ...defaultFilterQuery,
            [ReviewsStateUrlQueryNameEnum.ids]: '',
        });
        resetSavedFilter(defaultFilterQuery, getCurrentSavedFilterUiState());
    }

    const persistReviewsFilterQuery = useCallback(
        (nextPeriodPickerActiveTab?: ReviewsSavedFilterPeriodPickerTabType) => {
            const nextFilterQuery = reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form));

            setReviewsStateUrlQuery(nextFilterQuery);
            saveCurrentFilterStateDebounced(
                nextFilterQuery,
                nextPeriodPickerActiveTab
                    ? getCurrentSavedFilterUiState(nextPeriodPickerActiveTab)
                    : getCurrentSavedFilterUiState()
            );
        },
        [form, getCurrentSavedFilterUiState, saveCurrentFilterStateDebounced, setReviewsStateUrlQuery]
    );

    const onFieldsChange = useCallback(() => {
        if (isDeferredApply) {
            bumpDraftRevision();
            return;
        }

        persistReviewsFilterQuery();
    }, [bumpDraftRevision, isDeferredApply, persistReviewsFilterQuery]);

    const handleDrawerOpen = useCallback((): void => {
        syncFormFromAppliedState();
    }, [syncFormFromAppliedState]);

    const handleDrawerClose = useCallback((): void => {
        syncFormFromAppliedState();
        onDrawerClose?.();
    }, [onDrawerClose, syncFormFromAppliedState]);

    const handleDrawerApply = useCallback((): void => {
        const nextFilterQuery = reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form));
        const uiState = getCurrentSavedFilterUiState();

        if (draftSavedFilterEnabled !== isSavedFilterEnabled) {
            if (draftSavedFilterEnabled) {
                enableSavedFilter(nextFilterQuery, uiState);
            } else {
                disableSavedFilter();
            }
        }

        setReviewsStateUrlQuery(nextFilterQuery);
        saveCurrentFilterState(nextFilterQuery, uiState);
        onDrawerClose?.();
    }, [
        disableSavedFilter,
        draftSavedFilterEnabled,
        enableSavedFilter,
        form,
        getCurrentSavedFilterUiState,
        isSavedFilterEnabled,
        onDrawerClose,
        saveCurrentFilterState,
        setReviewsStateUrlQuery,
    ]);

    const handleDrawerReset = useCallback((): void => {
        const defaultFilterQuery = reviewsFilterStateToUrlObject(defaultReviewsStateMagnitIncluding);
        const defaultUiState = getCurrentSavedFilterUiState(
            savedFilterUiState.periodPickerActiveTab,
            getExpandedSectionsValue(false)
        );

        setExpandedSections(getExpandedSectionsValue(false));
        setReviewsStateUrlQuery({
            ...defaultFilterQuery,
            [ReviewsStateUrlQueryNameEnum.ids]: '',
        });
        resetSavedFilter(defaultFilterQuery, defaultUiState);
        onDrawerClose?.();
    }, [
        defaultReviewsStateMagnitIncluding,
        getCurrentSavedFilterUiState,
        onDrawerClose,
        resetSavedFilter,
        savedFilterUiState.periodPickerActiveTab,
        setReviewsStateUrlQuery,
    ]);

    function handleSavedFilterToggle() {
        if (isSavedFilterLoading || isSavedFilterSaving) {
            return;
        }

        if (isDeferredApply) {
            setDraftSavedFilterEnabled((previousState) => !previousState);
            return;
        }

        if (isSavedFilterEnabled) {
            disableSavedFilter();

            return;
        }

        enableSavedFilter(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState()
        );
    }

    function handleSavedFilterKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        handleSavedFilterToggle();
    }

    function handleDoctorsChange(value: Array<string>) {
        const lastSelected = value[value.length - 1];

        if (lastSelected === DoctorsReviewsEnum.all) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.doctorNames]: [DoctorsReviewsEnum.all]});
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.doctorNames]: value.filter((value_) => value_ !== DoctorsReviewsEnum.all),
            });
        }

        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.doctorNames, value);
    }

    function handleSourceListChange(value: Array<number>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.sourceList]: value});
        onFieldsChange();

        const sources = reviewsCatalogList
            ? reviewsCatalogList
                  .filter((catalog) => value.includes(catalog.id))
                  .map((catalog) => ({id: catalog.id, name: catalog.name}))
            : [];

        trackAnalytics(
            ReviewsFilterFieldNameEnum.sourceList,
            sources.map((source) => source.name)
        );
    }

    function handleRatingChange(value: Array<number>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.ratingList]: value});
        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.ratingList, value);
    }

    function handleResponsibleEmployeeChange(value: Array<number | string>) {
        const lastSelected = value[value.length - 1];

        if (lastSelected === EmployeesReviewsEnum.all) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.employeesResponsible]: [EmployeesReviewsEnum.all]});
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.employeesResponsible]: value.filter(
                    (value_) => value_ !== EmployeesReviewsEnum.all
                ),
            });
        }

        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.employeesResponsible, value);
    }

    function handleRespondingEmployeeChange(value: Array<number | string>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.employeesResponding]: value});
        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.employeesResponding, value);
    }

    function handleStatusListChange(
        _value: Array<string>,
        selectOptions: DefaultOptionType | Array<DefaultOptionType>
    ) {
        const customProcessStatus = (selectOptions as Array<CustomOptionType>)
            .filter((option) => !option.statusInfo.isDefault)
            .map((status) => status.statusInfo.statusCode);
        const defaultProcessStatus = (selectOptions as Array<CustomOptionType>)
            .filter((option) => option.statusInfo.isDefault)
            .map((status) => status.statusInfo.statusCode);

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.processStatus]: defaultProcessStatus,
            [ReviewsFilterFieldNameEnum.customProcessStatus]: customProcessStatus,
        });
        onFieldsChange();
        trackAnalytics(ReviewsFilterFieldNameEnum.processStatus, defaultProcessStatus);
        trackAnalytics(ReviewsFilterFieldNameEnum.customProcessStatus, customProcessStatus);
    }

    const handleNewTagsChange = useCallback(
        (value: Array<number | string>) => {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.tagList]: value});
            onFieldsChange();
            trackAnalytics(ReviewsFilterFieldNameEnum.tagList, value);
        },
        [form, onFieldsChange]
    );

    const handleAgencyRepliersChange = useCallback(
        (value: Array<number | string>) => {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.agencyRepliers]: value});
            onFieldsChange();
        },
        [form, onFieldsChange]
    );

    function handleWithoutTagChange(value: WithoutTagEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.withoutTag]: value});

        if (value === WithoutTagEnum.withoutTagTrue) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.tagList]: []});
        }

        onFieldsChange();

        const eventPayload = {
            [WithoutTagEnum.noSelected]: 'show all',
            [WithoutTagEnum.withoutTagTrue]: 'without tags',
            [WithoutTagEnum.withoutTagFalse]: 'with tags',
            null: 'all tags',
        }[value as WithoutTagEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.withoutTag, eventPayload);
    }

    function handleAutoReplyChange(value: Array<AutoReplayEnum>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.autoReplay]: value});

        trackAnalytics(ReviewsFilterFieldNameEnum.autoReplay, value);
    }

    function handleContentOfReviewsChange(value: ContentOfReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.contentOfReviews]: value});
        onFieldsChange();

        const type = {
            [ContentOfReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [ContentOfReviewsEnum.reviewsWithText]: 'with text',
            [ContentOfReviewsEnum.reviewsWithoutText]: 'without text',
        }[value as ContentOfReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.contentOfReviews, type);
    }

    function handleHasNotesChange(value: HasNotesEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.hasNotes]: value});
        onFieldsChange();

        const type = {
            [HasNotesEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [HasNotesEnum.yes]: 'yes',
            [HasNotesEnum.no]: 'no',
        }[value];

        trackAnalytics(ReviewsFilterFieldNameEnum.hasNotes, type);
    }

    function handleHiddenReviewsChange(value: HiddenReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.hiddenReviews]: value});
        onFieldsChange();

        const type = {
            [HiddenReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [HiddenReviewsEnum.yes]: 'yes',
            [HiddenReviewsEnum.no]: 'no',
        }[value];

        trackAnalytics(ReviewsFilterFieldNameEnum.hiddenReviews, type);
    }

    function handleDeletedReviewsChange(value: DeletedReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.deletedReviews]: value});
        onFieldsChange();

        const type = {
            [DeletedReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [DeletedReviewsEnum.yes]: 'yes',
            [DeletedReviewsEnum.no]: 'no',
        }[value as DeletedReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.deletedReviews, type);
    }

    function handleRepliesToReviewsChange(value: RepliesToReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.repliesToReviews]: value});
        onFieldsChange();

        const type = {
            [RepliesToReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [RepliesToReviewsEnum.ReviewsUnanswered]: 'without response',
            [RepliesToReviewsEnum.ReviewsWithAResponse]: 'with response',
        }[value as RepliesToReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.repliesToReviews, type);
    }

    function handlePossibilityOfAnswerChange(value: PossibilityOfAnAnswerEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: value});
        onFieldsChange();

        const type = {
            [PossibilityOfAnAnswerEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [PossibilityOfAnAnswerEnum.HasNoPossibilityOfAnAnswer]: 'has no possibility to answer',
            [PossibilityOfAnAnswerEnum.HasPossibilityOfAnAnswer]: 'has possibility to answer',
        }[value as PossibilityOfAnAnswerEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.possibilityOfAnAnswer, type);
    }

    function handleReviewTypeChange(value: ReviewNameEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewType]: value});
        onFieldsChange();

        const type = {
            [ReviewNameEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [ReviewNameEnum.ReviewsForCompanies]: 'for companies',
            [ReviewNameEnum.ReviewsForTheBrand]: 'for brand',
        }[value as ReviewNameEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.reviewType, type);
    }

    function handleAbuseStatusChange(value: Array<AbuseStatusEnum>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.abuseStatus]: value});
        onFieldsChange();
        trackAnalytics(ReviewsFilterFieldNameEnum.abuseStatus, value);
    }

    function handleTonalityChange(value: Array<ReviewsTonalityFilterEnum>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.sentiments]: value});
        onFieldsChange();
        trackAnalytics(ReviewsFilterFieldNameEnum.sentiments, value);
    }

    function handleAutoAbuseChange(value?: AutoAbuseEnum | null) {
        const next: AutoAbuseEnum =
            value === AutoAbuseEnum.yes || value === AutoAbuseEnum.no ? value : AutoAbuseEnum.notSelected;

        form.setFieldsValue({[ReviewsFilterFieldNameEnum.autoAbuse]: next});

        trackAnalytics(ReviewsFilterFieldNameEnum.autoAbuse, next === AutoAbuseEnum.notSelected ? '' : String(next));
    }

    function handleReviewChangedChange(value: ReviewChangedEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewChanged]: value});
        onFieldsChange();

        const type = {
            [ReviewChangedEnum.yes]: 'yes',
            [ReviewChangedEnum.no]: 'no',
            [ReviewChangedEnum.noSelected]: 'no selected',
        }[value as ReviewChangedEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.reviewChanged, type);
    }

    function handleWaitingForSecondAnswerChange(value: WaitingForSecondAnswerEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: value});
        onFieldsChange();

        const type = {
            [WaitingForSecondAnswerEnum.yes]: 'yes',
            [WaitingForSecondAnswerEnum.no]: 'no',
            [WaitingForSecondAnswerEnum.noSelected]: 'no selected',
        }[value as WaitingForSecondAnswerEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.waitingForSecondAnswer, type);
    }

    function handleModeratedReviewChange(value: ModeratedEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewModerated]: value});
        onFieldsChange();
    }

    function handleReplyPublishedChange(value: ReplyPublishedEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.replyPublished]: value});
        onFieldsChange();
    }

    function handleDataRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.dateFrom]: from,
            [ReviewsFilterFieldNameEnum.dateTo]: to,
        });
        onFieldsChange();

        trackAnalytics('Period', `${from} - ${to}`);
    }

    function handleCollectionDateRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.collectionDateFrom]: from,
            [ReviewsFilterFieldNameEnum.collectionDateTo]: to,
        });
        onFieldsChange();
    }

    function handleEmployeeReplyDateRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.replyDateFrom]: from,
            [ReviewsFilterFieldNameEnum.replyDateTo]: to,
        });
        onFieldsChange();

        trackAnalytics('Employee reply date', `${from} - ${to}`);
    }

    const hasDoctors = reviewsDoctorList?.results && reviewsDoctorList.results.length > 0;

    const watchedDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.dateFrom, form);
    const watchedDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.dateTo, form);
    const watchedCollectionDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.collectionDateFrom, form);
    const watchedCollectionDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.collectionDateTo, form);
    const watchedReplyDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.replyDateFrom, form);
    const watchedReplyDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.replyDateTo, form);

    const periodPickerValue = useMemo((): NoUndefinedRangeValueType<Dayjs> => {
        const publication: NoUndefinedRangeValueType<Dayjs> = [watchedDateFrom ?? dateFrom, watchedDateTo ?? dateTo];
        const collection: NoUndefinedRangeValueType<Dayjs> = [
            watchedCollectionDateFrom ?? collectionDateFrom,
            watchedCollectionDateTo ?? collectionDateTo,
        ];

        return periodPickerActiveTab === 'collection' ? collection : publication;
    }, [
        periodPickerActiveTab,
        watchedDateFrom,
        watchedDateTo,
        watchedCollectionDateFrom,
        watchedCollectionDateTo,
        dateFrom,
        dateTo,
        collectionDateFrom,
        collectionDateTo,
    ]);

    function handlePeriodCalendarChange(dates: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = dates || [null, null];

        if (periodPickerActiveTab === 'collection') {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.collectionDateFrom]: from,
                [ReviewsFilterFieldNameEnum.collectionDateTo]: to,
            });
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.dateFrom]: from,
                [ReviewsFilterFieldNameEnum.dateTo]: to,
            });
        }
    }

    /* eslint-disable complexity */
    function handlePeriodPickerTabChange(activeKey: string) {
        const nextActiveTab: ReviewsSavedFilterPeriodPickerTabType =
            activeKey === 'collection' ? 'collection' : 'publication';

        const currentFrom =
            (periodPickerActiveTab === 'collection'
                ? form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateFrom) ?? collectionDateFrom
                : form.getFieldValue(ReviewsFilterFieldNameEnum.dateFrom) ?? dateFrom) ?? null;
        const currentTo =
            (periodPickerActiveTab === 'collection'
                ? form.getFieldValue(ReviewsFilterFieldNameEnum.collectionDateTo) ?? collectionDateTo
                : form.getFieldValue(ReviewsFilterFieldNameEnum.dateTo) ?? dateTo) ?? null;

        if (nextActiveTab === 'collection') {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.collectionDateFrom]: currentFrom,
                [ReviewsFilterFieldNameEnum.collectionDateTo]: currentTo,
                [ReviewsFilterFieldNameEnum.dateFrom]: null,
                [ReviewsFilterFieldNameEnum.dateTo]: null,
            });
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.dateFrom]: currentFrom,
                [ReviewsFilterFieldNameEnum.dateTo]: currentTo,
                [ReviewsFilterFieldNameEnum.collectionDateFrom]: null,
                [ReviewsFilterFieldNameEnum.collectionDateTo]: null,
            });
        }

        setPeriodPickerActiveTab(nextActiveTab);

        if (isDeferredApply) {
            bumpDraftRevision();
            return;
        }

        persistReviewsFilterQuery(nextActiveTab);
        saveCurrentFilterState(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState(nextActiveTab)
        );
    }
    /* eslint-enable complexity */

    useEffect(() => {
        setPeriodPickerActiveTab(savedFilterUiState.periodPickerActiveTab);
    }, [savedFilterUiState.periodPickerActiveTab]);

    useEffect(() => {
        setExpandedSections(getExpandedSectionsValue(isSavedFilterEnabled, savedFilterUiState.expandedSections));
    }, [isSavedFilterEnabled, savedFilterUiState.expandedSections]);

    useEffect(() => {
        const hasCollection =
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom]) &&
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo]);
        const hasPublication =
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom]) &&
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateTo]);

        if (hasCollection && !hasPublication) {
            setPeriodPickerActiveTab('collection');
        }

        if (hasPublication && !hasCollection) {
            setPeriodPickerActiveTab('publication');
        }
    }, [
        reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom],
        reviewFilterState[ReviewsFilterFieldNameEnum.dateTo],
        reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom],
        reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo],
    ]);

    useEffect(() => {
        if (isDeferredApply) {
            return;
        }

        form.setFieldsValue(reviewFilterState);
        applyReviewFilterDateFieldsToForm(form, reviewFilterState);
    }, [form, isDeferredApply, reviewFilterState]);

    const sourcesOptions = useMemo(
        () =>
            reviewsCatalogList
                ? reviewsCatalogList.map((catalog) => ({value: catalog.id, label: catalog.name || ''}))
                : [],
        [reviewsCatalogList]
    );

    useEffect(() => {
        const noPublicationDates =
            isNil(queries[ReviewsFilterFieldNameEnum.dateFrom]) && isNil(queries[ReviewsFilterFieldNameEnum.dateTo]);
        const noCollectionDates =
            isNil(queries[ReviewsFilterFieldNameEnum.collectionDateFrom]) &&
            isNil(queries[ReviewsFilterFieldNameEnum.collectionDateTo]);
        const noReplyDates =
            isNil(queries[ReviewsFilterFieldNameEnum.replyDateFrom]) &&
            isNil(queries[ReviewsFilterFieldNameEnum.replyDateTo]);

        if (isBigBrand && noPublicationDates && noCollectionDates && noReplyDates) {
            const todayRaw = dayjs();
            const thirtyDaysAgoRaw = dayjs().subtract(30, 'day');

            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.dateFrom]: thirtyDaysAgoRaw,
                [ReviewsFilterFieldNameEnum.dateTo]: todayRaw,
                [ReviewsFilterFieldNameEnum.collectionDateFrom]: null,
                [ReviewsFilterFieldNameEnum.collectionDateTo]: null,
                [ReviewsFilterFieldNameEnum.replyDateFrom]: null,
                [ReviewsFilterFieldNameEnum.replyDateTo]: null,
            });

            if (!isDeferredApply) {
                onFieldsChange();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, isDeferredApply, queries, reviewFilterState]);

    useEffect(() => {
        if (!reviewStatusList?.statuses) {
            return;
        }

        const validCodes = new Set(reviewStatusList.statuses.map((status) => status.statusCode));
        const currentDefault = reviewFilterState[ReviewsFilterFieldNameEnum.processStatus];
        const currentCustom = reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus];
        const cleanedDefault = currentDefault.filter((code) => validCodes.has(code));
        const cleanedCustom = currentCustom.filter((code) => validCodes.has(code));

        if (cleanedDefault.length !== currentDefault.length || cleanedCustom.length !== currentCustom.length) {
            const updatedFilterState = {
                ...reviewFilterState,
                [ReviewsFilterFieldNameEnum.processStatus]: cleanedDefault,
                [ReviewsFilterFieldNameEnum.customProcessStatus]: cleanedCustom,
            };

            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.processStatus]: cleanedDefault,
                [ReviewsFilterFieldNameEnum.customProcessStatus]: cleanedCustom,
            });
            setReviewsStateUrlQuery(reviewsFilterStateToUrlObject(updatedFilterState));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewStatusList?.statuses]);

    const reviewsFilterSectionActiveMap = useMemo((): Record<ReviewsFilterSectionEnum, boolean> => {
        const uiDateFrom = activeFilterStateForUi[ReviewsFilterFieldNameEnum.dateFrom];
        const uiDateTo = activeFilterStateForUi[ReviewsFilterFieldNameEnum.dateTo];
        const uiCollectionDateFrom = activeFilterStateForUi[ReviewsFilterFieldNameEnum.collectionDateFrom];
        const uiCollectionDateTo = activeFilterStateForUi[ReviewsFilterFieldNameEnum.collectionDateTo];
        const uiReplyDateFrom = activeFilterStateForUi[ReviewsFilterFieldNameEnum.replyDateFrom];
        const uiReplyDateTo = activeFilterStateForUi[ReviewsFilterFieldNameEnum.replyDateTo];

        return {
            [ReviewsFilterSectionEnum.main]: isReviewsFilterMainSectionActive({
                reviewFilterState: activeFilterStateForUi,
                dateFrom: uiDateFrom ? dayjs(uiDateFrom) : null,
                dateTo: uiDateTo ? dayjs(uiDateTo) : null,
                collectionDateFrom: uiCollectionDateFrom ? dayjs(uiCollectionDateFrom) : null,
                collectionDateTo: uiCollectionDateTo ? dayjs(uiCollectionDateTo) : null,
                hasDoctors,
            }),
            [ReviewsFilterSectionEnum.employee]: isReviewsFilterEmployeeSectionActive(
                activeFilterStateForUi,
                uiReplyDateFrom ? dayjs(uiReplyDateFrom) : null,
                uiReplyDateTo ? dayjs(uiReplyDateTo) : null
            ),
            [ReviewsFilterSectionEnum.tags]: isReviewsFilterTagsSectionActive(activeFilterStateForUi),
            [ReviewsFilterSectionEnum.additional]: isReviewsFilterAdditionalSectionActive(activeFilterStateForUi),
        };
    }, [activeFilterStateForUi, hasDoctors]);

    const ratingOptions = useMemo(() => {
        const endpointRatings = Object.keys(reviewsStatisticResult?.ratingList || {}).map(Number);
        const mergedRatings = [...new Set([...RATING_FILTER_ORDER, ...endpointRatings])];

        return mergedRatings.map((rating) => ({
            rating,
            count: getRatingListCountForKey(reviewsStatisticResult?.ratingList, rating),
        }));
    }, [reviewsStatisticResult?.ratingList]);

    const totalReviewsLabelCount = useMemo(() => {
        if (!reviewsStatisticResult) {
            return 0;
        }

        const selectedRatings = activeFilterStateForUi[ReviewsFilterFieldNameEnum.ratingList];

        if (selectedRatings.length === 0) {
            return reviewsStatisticResult.count;
        }

        return selectedRatings.reduce(
            (sum, rating) => sum + getRatingListCountForKey(reviewsStatisticResult.ratingList, rating),
            0
        );
    }, [activeFilterStateForUi, reviewsStatisticResult]);

    const displayedAverageRating = useMemo(() => {
        if (!reviewsStatisticResult) {
            return 0;
        }

        const selectedRatings = activeFilterStateForUi[ReviewsFilterFieldNameEnum.ratingList];

        return getWeightedAverageRatingForSelectedRatings(
            reviewsStatisticResult.ratingList,
            selectedRatings,
            reviewsStatisticResult.ratingAvg
        );
    }, [activeFilterStateForUi, reviewsStatisticResult]);

    return {
        // form
        form,
        onFieldsChange,
        onFormReset,
        handleDrawerOpen,
        handleDrawerClose,
        handleDrawerApply,
        handleDrawerReset,

        // state
        expandedSections,
        periodPickerActiveTab,
        reviewFilterState: activeFilterStateForUi,
        reviewsFilterSectionActiveMap,
        isFilterEmpty: isFilterEmptyForUi,

        // period picker
        periodPickerValue,
        watchedReplyDateFrom,
        watchedReplyDateTo,
        replyDateFrom,
        replyDateTo,

        // rating
        ratingOptions,
        totalReviewsLabelCount,
        displayedAverageRating,
        isReviewsStatisticFetching,

        // sources
        sourcesOptions,
        reviewsCatalogListIsInProgress,

        // doctors
        hasDoctors,
        reviewsDoctorList,
        reviewsDoctorListIsInProgress,

        // statuses
        reviewStatusList,
        isReviewStatusListInProgress,

        // employees
        responsibleUsersList,
        isReviewResponsibleUsersInProgress,
        reviewsReplierList,
        reviewsReplierListIsInProgress,
        internalAgencyReviewLicense,

        // tags
        isShowAutotags,

        // saved filter
        isSavedFilterEnabled: displaySavedFilterEnabled,
        isSavedFilterLoading,
        isSavedFilterSaving,

        // localization
        getLocalizedString,

        // handlers
        handleSectionsChange,
        handleSavedFilterToggle,
        handleSavedFilterKeyDown,
        handleDoctorsChange,
        handleSourceListChange,
        handleRatingChange,
        handleResponsibleEmployeeChange,
        handleRespondingEmployeeChange,
        handleStatusListChange,
        handleNewTagsChange,
        handleAgencyRepliersChange,
        handleWithoutTagChange,
        handleAutoReplyChange,
        handleContentOfReviewsChange,
        handleHasNotesChange,
        handleHiddenReviewsChange,
        handleDeletedReviewsChange,
        handleRepliesToReviewsChange,
        handlePossibilityOfAnswerChange,
        handleReviewTypeChange,
        handleAbuseStatusChange,
        handleTonalityChange,
        handleAutoAbuseChange,
        handleReviewChangedChange,
        handleWaitingForSecondAnswerChange,
        handleModeratedReviewChange,
        handleReplyPublishedChange,
        handleDataRangeChange,
        handleCollectionDateRangeChange,
        handleEmployeeReplyDateRangeChange,
        handlePeriodCalendarChange,
        handlePeriodPickerTabChange,
    };
}
